import React, { useCallback, useState, useRef } from 'react';
import { Modal, Dropdown } from '@workboard/wobo-ui';
import { TFunction, useTranslation } from 'react-i18next';
import { dialogContentClasses, Stack } from '@mui/material';
import styled from 'styled-components';
import he from 'he';
import MetricOwnerCommonModal from '../metric-owner.component';
import { MetricObj, SearchUser, User } from './cascadeKrTypes';
import { ErrorTextComp } from '../../../../inputs/fields.component';

type CascadeKrCreateModalProps = {
  metricObj: MetricObj;
  realOwner: User;
  teamId: string;
  onClose: () => void;
  onSave: (metric: MetricObj) => void;
  isOldDis: boolean;
  intervalOptions: { value: number; name: string }[];
};

const StyledModal = styled(Modal)`
  z-index: 1000000 !important;

  &&& .${dialogContentClasses.root} {
    padding: 16px 24px 24px 24px;
    overflow: visible;
  }
`;

const StyledPaperProps = {
  'aria-modal': true,
  'data-testid': 'wbui-modal',
  style: {
    minWidth: '769px',
  },
};

const Container = styled(Stack)`
  gap: 10px;
`;

const Label = styled.div`
  color: #333943;

  /* Body/Default */
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: 0.15px;
`;

const KrNameTextArea = styled.textarea`
  display: flex;
  height: 72px;
  padding: 8px 8px 8px 12px;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 16px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(0, 0, 0, 0.06);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Text = styled.span`
  margin-top: 8px;
`;

const TextInput = styled.input`
  width: 110px;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 113px !important;
  max-width: fit-content;
  height: 39px !important;
  & > svg {
    margin-right: 10px !important;
  }
  .MuiSelect-root {
    background-color: #f2f2f2 !important;
    border: 1px solid rgba(0, 0, 0, 0.06);
    height: 38px;
  }
  .MuiSelect-select.Mui-disabled {
    .MuiListItemText-root {
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }
  .MuiSelect-icon.Mui-disabled {
    display: none;
  }
  #unit-measure-dropdown {
    min-width: 177px;
  }
  #cal-overall-dropdown {
    min-width: 226px;
  }
  #select-day-dropdown {
    min-width: 120px;
  }
  #select-interval-dropdown {
    min-width: 113px;
  }
  #result-value-dropdown {
    min-width: 220px;
  }
  #calculate-update-as-dropdown {
    min-width: 224px;
  }
`;

const Title = ({ t }: { t: TFunction }) => (
  <div>
    <div className="icon icon-cascade-18px" style={{ marginRight: '12px', marginBottom: '2px' }} />
    <span>{t('Create Cascade Key Result')}</span>
  </div>
);

export const TEMP_ID_PREFIX = 'tmpId';

export const CascadeKrCreateModal = ({
  teamId,
  metricObj,
  realOwner,
  isOldDis,
  intervalOptions,
  onClose,
  onSave,
}: CascadeKrCreateModalProps) => {
  const { t } = useTranslation();
  const [owner, setOwner] = useState<User>(!!metricObj.owner && typeof metricObj.owner === 'object' ?
    (metricObj.owner as User) :
    realOwner);
  const targetInputRef = useRef<HTMLInputElement>(null);
  const initialDataInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLTextAreaElement>(null);
  const [interval, setInterval] = useState<number>(metricObj.interval);
  const [validation, setValidation] = useState<Record<string, string>>({});

  const changeInterval = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setInterval(Number(event.target.value));
  }, []);

  const updateOwner = useCallback(
    (newOwner: SearchUser) => {
      setOwner({
        id: newOwner.userId,
        name: newOwner.userText,
        avatar: newOwner.userPic,
      });
    },
    [setOwner],
  );

  const validateForm = useCallback(() => {
    const nameValue = nameInputRef.current?.value;
    const initialDataValue = initialDataInputRef.current?.value;
    const targetValue = targetInputRef.current?.value;
    const newValidation = {
      name: nameValue ? '' : t('Enter value'),
      initial_data: initialDataValue ? '' : t('Enter value'),
      target: targetValue ? '' : t('Enter value'),
    };
    return newValidation;
  }, [nameInputRef, initialDataInputRef, targetInputRef, t]);

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name } = e.target;
      const newValidation = validateForm();
      setValidation((prevValidation) => ({ ...prevValidation, [name]: newValidation[name] }));
    },
    [validateForm],
  );

  const handleSave = useCallback(() => {
    const randomId = `${TEMP_ID_PREFIX}_${Math.random().toString(36).substring(2, 15)}`;
    const idToUse =
      typeof metricObj.id === 'string' && metricObj.id.startsWith(TEMP_ID_PREFIX)
        ? metricObj.id
        : randomId;
    const nameValue = nameInputRef.current?.value;
    const initialDataValue = initialDataInputRef.current?.value;
    const targetValue = targetInputRef.current?.value;
    const newValidation = validateForm();
    setValidation(newValidation);
    if (Object.values(newValidation).some((v) => v)) {
      return;
    }
    onSave({
      ...metricObj,
      id: idToUse,
      name: he.encode(nameValue),
      target: targetValue,
      initial_data: initialDataValue ? Number(initialDataValue) : 0,
      interval,
      owner_id: owner.id,
      owner,
      validation: '',
    });
    onClose();
  }, [onSave, onClose, metricObj, owner, interval, validateForm]);

  return (
    <StyledModal
      isOpen
      title={<Title t={t} />}
      onDismiss={onClose}
      onSubmit={handleSave}
      PaperProps={StyledPaperProps}
      submitLabel={t('Save')}
    >
      <Container className="aE-Edit-Metric" style={{ paddingTop: '0px' }}>
        <Field>
          <Label>
            {t('Key Result Name')}
            <span className="text-style-1"> *</span>
          </Label>
          <KrNameTextArea
            name="name"
            placeholder={t('Enter value')}
            defaultValue={metricObj.name}
            autoComplete="off"
            ref={nameInputRef}
            onBlur={handleInputBlur}
          />
          {validation.name ? <ErrorTextComp errors={validation} field="name" /> : <div>&nbsp;</div>}
        </Field>
        <Field>
          <Stack direction="row" alignItems="baseline" gap="8px">
            <Field>
              <Label>
                {t('Starting value')}
                <span className="text-style-1"> *</span>
              </Label>
              <TextInput
                ref={initialDataInputRef}
                className="input-text"
                type="number"
                placeholder={t('Enter value')}
                name="initial_data"
                autoComplete="off"
                defaultValue={metricObj.initial_data}
                onBlur={handleInputBlur}
              />
              {validation.initial_data ? <ErrorTextComp errors={validation} field="initial_data" /> : <div>&nbsp;</div>}
            </Field>
            <Field>
              <Label>&nbsp;</Label>
              <Text>{t('to')}</Text>
              <div>&nbsp;</div>
            </Field>
            <Field>
              <Label>
                {t('Target value')}
                <span className="text-style-1"> *</span>
              </Label>
              <TextInput
                ref={targetInputRef}
                className="input-text"
                type="number"
                placeholder={t('Enter value')}
                name="target"
                autoComplete="off"
                defaultValue={metricObj.target}
                onBlur={handleInputBlur}
              />
              {validation.target ? <ErrorTextComp errors={validation} field="target" /> : <div>&nbsp;</div>}
            </Field>
            <Field>
              <Label>{t('Owner')}</Label>
              <MetricOwnerCommonModal
                teamId={teamId}
                addId="cascadeMetrictarttargetsecId"
                item={owner}
                updateData={updateOwner}
                scrollElClass="krRightPaneContent"
              />
              <div>&nbsp;</div>
            </Field>
            <Field>
              <Label>&nbsp;</Label>
              <Text style={{ marginLeft: '-10px' }}>{t('to update')}</Text>
              <div>&nbsp;</div>
            </Field>
            <Field>
              <Label>&nbsp;</Label>
              <StyledDropdown
                id="select-interval-dropdown"
                name="interval"
                aria-label={t('How often to update?')}
                value={Number(interval)}
                onChange={changeInterval}
                required
                autoMenuWidth
                optionTitleProp="name"
                optionValueProp="value"
                disabled={isOldDis}
                options={intervalOptions}
                MenuProps={{
                  style: {
                    zIndex: '1500000',
                  },
                }}
              />
              <div>&nbsp;</div>
            </Field>
          </Stack>
        </Field>
      </Container>
    </StyledModal>
  );
};
